import {z} from 'zod'
import {TimeSlot} from '@/features/users-availability/types'

// Time constants
export const DEFAULT_START_TIME = '08:00'
export const DEFAULT_END_TIME = '20:00'
export const DEFAULT_TIME_INTERVAL = 15

// Form options
export const FIELD_OPTIONS = {
    shouldValidate: true,
    shouldDirty: true
} as const

// Convert time string (HH:MM) to minutes since midnight
export const timeToMinutes = (timeStr: string): number => {
    const [hours, minutes] = timeStr.split(':').map(Number)
    return hours * 60 + minutes
}

// Helper function to check if two time slots overlap
const doTimeSlotsOverlap = (slot1: TimeSlot, slot2: TimeSlot): boolean => {
    // Skip validation for incomplete slots
    if (!slot1.start || !slot1.end || !slot2.start || !slot2.end) return false

    // Convert time strings to comparable values (minutes since midnight)
    const getMinutes = (timeStr: string): number => {
        const [hours, minutes] = timeStr.split(':').map(Number)
        return hours * 60 + minutes
    }

    const start1 = getMinutes(slot1.start)
    const end1 = getMinutes(slot1.end)
    const start2 = getMinutes(slot2.start)
    const end2 = getMinutes(slot2.end)

    // Check for overlap
    return start1 < end2 && start2 < end1
}

// Helper function to check if a time slot is a duplicate of another
const isDuplicateTimeSlot = (slot1: TimeSlot, slot2: TimeSlot): boolean => {
    // Skip validation for incomplete slots
    if (!slot1.start || !slot1.end || !slot2.start || !slot2.end) return false

    return slot1.start === slot2.start && slot1.end === slot2.end
}

// Helper function to check if end time is after start time
const isEndTimeAfterStartTime = (slot: TimeSlot): boolean => {
    // Skip validation for incomplete slots
    if (!slot.start || !slot.end) return true

    const getMinutes = (timeStr: string): number => {
        const [hours, minutes] = timeStr.split(':').map(Number)
        return hours * 60 + minutes
    }

    const startMinutes = getMinutes(slot.start)
    const endMinutes = getMinutes(slot.end)

    return endMinutes > startMinutes
}

// Helper function to validate time slot is not empty
const isTimeSlotComplete = (slot: TimeSlot): boolean => {
    return !!(slot.start && slot.end && slot.start.trim() !== '' && slot.end.trim() !== '')
}

// Time slot schema with validation for end time being after start time
const ValidatedTimeSlotSchema = z
    .object({
        start: z.string().min(1, "L'orario di inizio è obbligatorio"),
        end: z.string().min(1, "L'orario di fine è obbligatorio")
    })
    .refine(slot => isEndTimeAfterStartTime(slot), {
        message: "L'orario di fine deve essere successivo all'orario di inizio",
        path: ['end'] // This indicates which field has the error
    })

// Enhanced day availability schema with validation
const EnhancedDayAvailabilitySchema = z
    .object({
        enabled: z.boolean(),
        timeSlots: z
            .array(ValidatedTimeSlotSchema)
            .refine(
                slots => {
                    // Skip validation if there are no slots or only one slot
                    if (slots.length <= 1) return true

                    // Check for duplicates and overlaps
                    for (let i = 0; i < slots.length; i++) {
                        if (!slots[i].start || !slots[i].end) continue

                        for (let j = i + 1; j < slots.length; j++) {
                            if (!slots[j].start || !slots[j].end) continue

                            if (isDuplicateTimeSlot(slots[i], slots[j]) || doTimeSlotsOverlap(slots[i], slots[j])) {
                                return false
                            }
                        }
                    }
                    return true
                },
                {
                    message: 'Le fasce orarie non possono sovrapporsi o essere duplicate nello stesso giorno'
                }
            )
            .default([])
    })
    // Use a separate function to check for complete time slots without showing error messages
    .superRefine((day, ctx) => {
        // If the day is enabled, require at least one complete time slot
        if (day.enabled && day.timeSlots.length > 0) {
            const hasIncompleteSlots = day.timeSlots.some(slot => !isTimeSlotComplete(slot))

            if (hasIncompleteSlots) {
                // Add an issue without a message but with a code we can detect
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['timeSlots'],
                    message: ' ', // Use a space instead of empty string
                    fatal: true // Mark as fatal to block validation
                })
                return false
            }
        }
        return true
    })

// Create a schema that matches the UserAvailability type
export const AvailabilityFormValidationSchema = z.object({
    monday: EnhancedDayAvailabilitySchema,
    tuesday: EnhancedDayAvailabilitySchema,
    wednesday: EnhancedDayAvailabilitySchema,
    thursday: EnhancedDayAvailabilitySchema,
    friday: EnhancedDayAvailabilitySchema,
    saturday: EnhancedDayAvailabilitySchema,
    sunday: EnhancedDayAvailabilitySchema
})

export type AvailabilityFormValidationSchema = z.infer<typeof AvailabilityFormValidationSchema>
